import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { getIn } from 'formik';

import { NestedCheckboxDropDown } from '../../FilterComponents/NestedCheckboxDropdownOverlay/NestedCheckboxDropdownOverlay';
import { formatMenuLabelText, isMenuLabelTruncated } from '../../FilterComponents/GroupedCheckboxFilterMenu/menuLabelDisplay';
import { getSelectionId } from '../../FilterComponents/GroupedCheckboxFilterMenu/filterItemIds';

import styles from './index.module.css';

const renderSelectedChips = ({ selectedItems, setFieldValue, name }) => {
  if (!selectedItems?.length) return null;

  const removeById = (id) => (event) => {
    event.stopPropagation();
    event.preventDefault();
    const sid = String(id);
    const next = selectedItems.filter((item) => getSelectionId(item) !== sid);
    setFieldValue(name, next);
  };

  const visibleItems = selectedItems.slice(0, 2);
  const remainingCount = selectedItems.length - visibleItems.length;

  return (
    <>
      {visibleItems.map((item) => {
        const id = getSelectionId(item);
        const rawName = item?.name ?? '';
        const label = formatMenuLabelText(rawName);
        return (
          <span key={id || label} className={styles.chip}>
            <span className={styles.chipText} title={isMenuLabelTruncated(rawName) ? rawName : undefined}>
              {label}
            </span>
            <button
              type='button'
              className={styles.chipRemove}
              onClick={removeById(id)}
              aria-label={`Remove ${rawName || label}`}>
              ×
            </button>
          </span>
        );
      })}
      {remainingCount > 0 ? (
        <span className={styles.chipSummary} aria-label={`${remainingCount} more selected`}>
          +{remainingCount}...
        </span>
      ) : null}
    </>
  );
};

const renderDropDownComponent =
  ({ myClassName, selectedItems, setFieldValue, name, title }) =>
  ({ visible }) =>
    (
      <div
        className={classnames({
          [styles.headRow]: true,
          [myClassName]: myClassName,
          [styles.headRowActive]: visible,
          [styles.headRowDisabled]: !visible,
        })}>
        <span
          className={classnames({
            [styles.cellTitle]: true,
            [styles.cellTitleTags]: selectedItems.length > 0,
            [myClassName]: myClassName,
          })}>
          {selectedItems.length ? renderSelectedChips({ selectedItems, setFieldValue, name }) : title}
        </span>
        <DownOutlined
          className={classnames({
            [styles.icon]: true,
            [styles.disabled]: !visible,
          })}
        />
      </div>
    );

export const FilterNestedCheckboxListCell = ({ value, name, title, values, myClassName, setFieldValue, ...rest }) => (
  <NestedCheckboxDropDown
    {...rest}
    setFieldValue={setFieldValue}
    values={values}
    name={name}
    title={title}
    Component={renderDropDownComponent({
      myClassName,
      selectedItems: getIn(values, name) || [],
      setFieldValue,
      name,
      title,
    })}
  />
);

FilterNestedCheckboxListCell.propTypes = {
  value: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      value: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
      children: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          value: PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
          }),
        })
      ),
    })
  ),
  name: PropTypes.string,
  title: PropTypes.string,
  values: PropTypes.object,
  myClassName: PropTypes.string,
};